import { useTranslation } from "react-i18next";

import type { CcManifestVersion, CcObjectManifestHistory } from "@/api/code-companion";
import { useCcChangeSetObject, useCcManifests } from "@/hooks/useCodeCompanion";

/**
 * O objeto por dentro: a decisão que o gerou e o código que ele virou (S-064 onda 4).
 *
 * Duas perguntas que o board não respondia. **Por que este objeto foi redesenhado em vez
 * de convertido** — que está no manifesto, e o manifesto só era alcançável por tool MCP.
 * E **o que exatamente mudou** — que exige o legado, que nunca era guardado: buscado do
 * ECC na transformação, entregue ao modelo e descartado.
 *
 * **A versão substituída é o que interessa.** A leitura de decisão filtra `superseded`,
 * porque o change set precisa da versão vigente. Aqui é o contrário: uma decisão que
 * mudou de "convert" para "redesign" é a primeira coisa que um auditor quer ver.
 *
 * **`diff: null` não é diff vazio.** Objeto migrado antes desta onda não tem legado
 * guardado. Mostrar "sem alterações" ali seria afirmar que o código não mudou, quando o
 * que houve é que ninguém anotou o que ele era.
 */

/** A decisão em uma linha: é o que se lê primeiro, antes de abrir o JSON. */
export function decisionSummary(v: CcManifestVersion): string | null {
  const d = (v.manifest?.decision ?? {}) as Record<string, unknown>;
  const partes = [d.disposition, d.clean_core_level && `clean core ${d.clean_core_level}`]
    .filter(Boolean)
    .map(String);
  return partes.length ? partes.join(" · ") : null;
}

/** O pacote de destino, que é a outra metade da decisão. */
export function deliveryTarget(v: CcManifestVersion): string | null {
  const d = (v.manifest?.delivery ?? {}) as Record<string, unknown>;
  return d.package ? String(d.package) : null;
}

/** Cor por sinal do diff. Contexto é neutro; só adição e remoção pesam. */
export function diffTone(line: string): "add" | "del" | "meta" | "ctx" {
  if (line.startsWith("+++") || line.startsWith("---") || line.startsWith("@@")) return "meta";
  if (line.startsWith("+")) return "add";
  if (line.startsWith("-")) return "del";
  return "ctx";
}

const DIFF_COLOR: Record<string, string> = {
  add: "#2b8f57",
  del: "#b02a31",
  meta: "#6b7280",
  ctx: "inherit",
};

/** O `detail` que o FastAPI mandou, quando ele é texto.
 *
 * Num 422 o `detail` é lista de objetos, e renderizar isso como child estoura o React.
 * A recusa por nome ambíguo desta fase manda uma frase acionável ("existe em mais de um
 * tipo neste change set (BDEF, DDLS). Informe o tipo."), e é ela que precisa chegar.
 */
export function serverDetail(error: unknown): string | null {
  const d = (error as { response?: { data?: { detail?: unknown } } })?.response?.data
    ?.detail;
  return typeof d === "string" && d ? d : null;
}

interface Props {
  projectId: string;
  packageId: number;
  changeSetId: number;
  objectName: string;
  /** Tipo TADIR. Desempata pilha RAP, onde o mesmo nome é DDLS e BDEF. */
  objectType?: string;
}

export default function ObjectInspector({
  projectId,
  packageId,
  changeSetId,
  objectName,
  objectType,
}: Props) {
  const { t } = useTranslation();
  const detail = useCcChangeSetObject(projectId, changeSetId, objectName, objectType);
  const manifestos = useCcManifests(projectId, packageId);

  // ⚠️ O tipo entra aqui também. `get_manifest_history` agrupa por (nome, tipo), então
  // achar só por nome traz o primeiro da ordenação: abrir o DDLS mostrava o source certo
  // e a decisão do BDEF, que é o colapso RAP uma linha abaixo do conserto.
  const historico: CcObjectManifestHistory | undefined = manifestos.data?.items.find(
    (i) =>
      i.object_name === objectName && (!objectType || i.object_type === objectType),
  );

  return (
    <div
      data-testid="object-inspector"
      className="space-y-3 rounded-lg border border-border bg-card p-3"
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs font-semibold">{objectName}</span>
        {/* Do prop primeiro: com a query em voo ou em erro, `detalhe.data` é undefined e
            o cabeçalho mostraria o nome pelado, sem dizer qual dos irmãos foi clicado. */}
        {(objectType ?? detail.data?.object_type) && (
          <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
            {objectType ?? detail.data?.object_type}
          </span>
        )}
      </div>

      {/* a decisão */}
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Migration decision")}
        </div>
        {!historico ? (
          <div className="text-[11px] text-muted-foreground">
            {t("No manifest recorded for this object.")}
          </div>
        ) : (
          <ul data-testid="manifest-versions" className="space-y-1">
            {historico.versions.map((v) => (
              <li
                key={v.version}
                className="flex flex-wrap items-baseline gap-2 border-l-2 pl-2 text-[11px]"
                style={{
                  borderColor: v.version === historico.current_version ? "#2b8f57" : "#e2e8f0",
                }}
              >
                <span className="font-mono text-[10px] text-muted-foreground">
                  v{v.version}
                </span>
                <span
                  className="font-mono text-[10px] uppercase"
                  style={{ color: v.status === "superseded" ? "#94a3b8" : "inherit" }}
                >
                  {v.status}
                </span>
                {/* Marcado uma vez só, e pelo servidor: a tela reimplementando a
                    precedência mentiria no dia em que o gate mudasse. */}
                {v.version === historico.current_version && (
                  <span
                    data-testid="manifest-current"
                    className="rounded bg-emerald-50 px-1 font-mono text-[10px] text-emerald-700"
                  >
                    {t("in force")}
                  </span>
                )}
                {decisionSummary(v) && <span>{decisionSummary(v)}</span>}
                {deliveryTarget(v) && (
                  <span className="font-mono text-[10px] text-muted-foreground">
                    → {deliveryTarget(v)}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* o código */}
      <div>
        <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {t("Legacy to target")}
        </div>
        {detail.isLoading ? (
          <div className="text-[11px] text-muted-foreground">{t("Loading source...")}</div>
        ) : detail.isError ? (
          // ⚠️ Sem este ramo o 409 de ambiguidade caía no braço de baixo e a tela dizia
          // que o legado não foi guardado, que é falso: houve recusa por nome ambíguo. E
          // o detalhe acionável que o servidor escreveu não chegava a superfície nenhuma.
          <div data-testid="object-error" className="text-[11px] text-muted-foreground">
            {serverDetail(detail.error) ?? t("Could not read this object.")}
          </div>
        ) : detail.data?.diff === null || detail.data?.diff === undefined ? (
          // Ausência dita, não disfarçada de "igual".
          <div data-testid="diff-unknown" className="text-[11px] text-muted-foreground">
            {t("The legacy source was not kept for this object, so there is no diff.")}
          </div>
        ) : detail.data.diff.length === 0 ? (
          <div className="text-[11px] text-muted-foreground">{t("No changes.")}</div>
        ) : (
          <pre
            data-testid="object-diff"
            className="max-h-72 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px] leading-snug"
          >
            {detail.data.diff.map((line, i) => (
              <div key={i} style={{ color: DIFF_COLOR[diffTone(line)] }}>
                {line}
              </div>
            ))}
          </pre>
        )}
      </div>

      {/* ✅ **O teste gerado também é entrega** (#513). A API mandava `test_source` desde
          a CC.34 e nenhum componente o renderizava, então "o código gerado com diff" do
          critério de aceite chegava pela metade: o objeto aparecia, a prova de
          comportamento dele não. Fechado por padrão, como o cru do SAP. */}
      {detail.data?.test_source && (
        <details data-testid="object-test-source" className="mt-2">
          <summary className="cursor-pointer text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {t("Generated test")}
          </summary>
          <pre className="mt-1 max-h-72 overflow-auto rounded bg-muted/40 p-2 font-mono text-[10px] leading-snug">
            {detail.data.test_source}
          </pre>
        </details>
      )}
    </div>
  );
}
